import { ToolDefinition } from "../types";

const pdfDpiChangerTool: ToolDefinition = {
  slug: "pdf-dpi-changer",
  name: "PDF DPI Changer | Change PDF Resolution and PPI Online",
  title: "PDF DPI Changer",
  description:
    "Change PDF DPI online to optimize file size or prepare for print. Convert your PDF to 300 DPI, reduce resolution, and reprocess embedded images easily.",
  longDescription:
    "Easily change the DPI of your PDF online. Whether you need to convert a PDF to 300 DPI for high-quality printing or reduce PDF DPI to make a file smaller, our PDF resolution changer reprocesses embedded images to your exact specifications in seconds.",
  toolType: {
    label: "PDF Reprocessor",
    icon: "sliders",
  },
  heroBadges: [
    "Free Online Converter",
    "Set 1 to 1200 DPI",
    "No Installation Required",
  ],
  category: "PDF Tools",
  categorySlug: "pdf-tools",
  href: "/tools/pdf-dpi-changer",
  api: "/api/v1/pdf/dpi-changer",
  keywords: [
    "snappy pdf dpi changer",
    "change pdf dpi",
    "pdf resolution changer",
    "pdf ppi changer",
    "convert pdf to 300 dpi",
    "pdf dpi converter",
    "reduce pdf dpi",
    "set pdf dpi online",
    "pdf image dpi converter",
    "make pdf 300 dpi",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Change PDF DPI Online",
        description:
          "Follow these simple steps to set a new target resolution and reprocess the embedded images in your PDF document.",
      },
      benefits: {
        title: "Why Use Our PDF Resolution Changer?",
        description:
          "Quickly adapt your documents for print or digital distribution with precise control over your embedded image DPI.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Find answers about converting PDFs to 300 DPI, reducing file sizes, and understanding how embedded image reprocessing works.",
      },
    },
    intro: {
      title: "Adjust Your PDF Resolution for Print or Web",
      paragraphs: [
        "Managing the resolution of your documents is essential when preparing files for different mediums. A PDF designed for reading on a screen might be too low in quality for professional printing, while a high-resolution scanned PDF might be too large to send via email. Our PDF DPI changer bridges this gap by allowing you to manually set the target resolution for your file.",
        "When you change PDF DPI using our tool, it systematically scans your document for embedded raster images—such as photographs or scanned pages—and reprocesses them toward your requested value. If you need to convert a PDF to 300 DPI to meet commercial prepress requirements, simply enter 300. If you are trying to reduce PDF DPI to make a file smaller for online archiving, entering a lower value like 72 or 150 will compress the image data accordingly.",
        "It is important to understand that this tool operates by reprocessing existing data. While reducing the DPI will effectively shrink the file size and lower the resolution, increasing the DPI will stretch the existing pixels to meet the new target. It does not use AI to magically restore missing details or enhance visual quality beyond the source material. Instead, it ensures your file's metadata and structure comply with the strict PPI requirements of your target workflow.",
      ],
      note: "This tool changes the DPI of embedded raster images. It does not affect vector graphics or mathematically drawn text.",
    },
    introBlocks: [
      {
        kind: "table",
        tables: [
          {
            title: "Common DPI Settings and Their Uses",
            description:
              "Not sure which target resolution to choose? Use this quick reference guide to set the correct PDF DPI online.",
            columns: ["Target DPI", "Best Used For", "Effect on File Size"],
            rows: [
              {
                "Target DPI": "72 DPI",
                "Best Used For":
                  "Web viewing, screen display, email attachments",
                "Effect on File Size":
                  "Produces the smallest possible file size.",
              },
              {
                "Target DPI": "150 DPI",
                "Best Used For":
                  "Draft printing, standard office documents, archiving",
                "Effect on File Size":
                  "Medium file size with acceptable readability.",
              },
              {
                "Target DPI": "300 DPI",
                "Best Used For":
                  "Commercial printing, magazines, high-quality brochures",
                "Effect on File Size":
                  "Large file size. Industry standard for print.",
              },
              {
                "Target DPI": "600 - 1200 DPI",
                "Best Used For":
                  "Fine art printing, archival scanning, detailed blueprints",
                "Effect on File Size":
                  "Extremely large file size. Maximum data retention.",
              },
            ],
            note: "For most professional printing scenarios, converting your PDF to 300 DPI is the safest choice.",
            emphasisColumns: ["Target DPI"],
            monospaceColumns: [],
          },
        ],
      },
      {
        kind: "text",
        title: "How Reprocessing Embedded Images Works",
        level: "h2",
        paragraphs: [
          "Many users search for a way to make a PDF 300 DPI when their print shop rejects a submitted file. When you use our PDF image DPI converter to increase the resolution, the tool mathematically scales the embedded images to pack more dots per inch into the document's physical dimensions. This satisfies automated print-check software that flags files containing images under 300 PPI.",
          "Conversely, if you have a massive scanned document, you can use the PDF PPI changer to input a lower number. By dialing down the target resolution, the tool will permanently discard excess pixel data from the embedded graphics. This is the most effective way to reduce PDF DPI and shrink the overall file size without requiring expensive desktop software like Adobe Acrobat or Photoshop.",
        ],
        note: "Remember, increasing the DPI creates the required document structure for printers, but cannot add physical detail that was missing from the original photo.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Key Features of the PDF DPI Converter",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Custom Output Control",
                icon: "sliders",
                description:
                  "Enter any specific whole-number value between 1 and 1200 DPI to perfectly match your project requirements.",
                accent: "primary",
              },
              {
                label: "Instant Reprocessing",
                icon: "bolt",
                description:
                  "Upload, process, and download your updated PDF in a single, streamlined workflow.",
                accent: "primary",
              },
              {
                label: "Smart File Renaming",
                icon: "file-text",
                description:
                  "Downloaded files are automatically appended with your chosen DPI value (e.g., document_300dpi.pdf) for easy organization.",
                accent: "primary",
              },
            ],
          },
        ],
      },
    ],
    callouts: [
      {
        title: "Workflow Optimization",
        layout: "list",
        iconColor: "primary",
        items: [
          {
            label: "Preflight Ready",
            icon: "check-circle",
            description:
              "Ensure your document seamlessly passes strict commercial prepress validations by securing a true 300 DPI structure.",
            accent: "primary",
          },
          {
            label: "Email Friendly",
            icon: "mail",
            description:
              "Convert heavy, high-resolution scans into lightweight 72 DPI documents so they fit comfortably inside email attachment limits.",
            accent: "primary",
          },
        ],
      },
    ],
    referenceTables: [],
    faqs: [
      {
        question: "How do I change the DPI of a PDF?",
        answer:
          "Upload your file to our online PDF DPI converter, enter your desired target DPI (such as 300 for print or 72 for web), and click process. The tool will reprocess the embedded images and provide a downloadable updated PDF.",
      },
      {
        question: "Can I convert a PDF to 300 DPI?",
        answer:
          "Yes, you can set your target to 300 DPI. The tool will reprocess the document so the embedded images meet this specification, which is the standard requirement for professional printing.",
      },
      {
        question: "Does changing PDF DPI improve visual quality?",
        answer:
          "No. If your original images are blurry or low quality, increasing the DPI to 300 will not magically add missing details or sharpen the image. It only changes the file's resolution data and stretches the pixels to meet structural print requirements.",
      },
      {
        question: "How do I reduce PDF DPI to make a file smaller?",
        answer:
          "Simply upload your document and enter a lower DPI value, such as 72 or 150. The PDF resolution changer will compress the embedded images by discarding unnecessary pixel data, resulting in a much smaller file size.",
      },
      {
        question: "What is the difference between DPI and PPI in a PDF?",
        answer:
          "PPI (Pixels Per Inch) refers to the digital resolution of the images inside the PDF. DPI (Dots Per Inch) refers to how a physical printer outputs those pixels onto paper. In digital conversion tools, the terms are generally used interchangeably to refer to image resolution.",
      },
      {
        question: "Can scanned PDF resolution be changed online?",
        answer:
          "Yes. Scanned PDFs are essentially large raster images wrapped in a PDF container. Our PDF PPI changer can easily scale these embedded images up or down based on your target DPI input.",
      },
    ],
    benefits: [
      {
        icon: "printer",
        title: "Print-Ready Compliance",
        description:
          "Easily convert your PDF to 300 DPI to pass automated pre-flight checks at commercial print shops.",
      },
      {
        icon: "compress",
        title: "File Size Reduction",
        description:
          "Lower the DPI of heavy, image-laden documents to create smaller, fast-loading, email-friendly files.",
      },
      {
        icon: "browser",
        title: "Fully Browser-Based",
        description:
          "Change PDF resolution online without downloading or installing any complex editing software.",
      },
      {
        icon: "shield-check",
        title: "Secure Processing",
        description:
          "Your documents are processed securely and deleted automatically from our servers, keeping your private data safe.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload Your PDF",
        description:
          "Choose a PDF up to 20 MB. Our system supports documents up to 5000 pages for rapid processing.",
      },
      {
        number: "02",
        title: "Enter Target DPI",
        description:
          "Set a whole-number DPI value from 1 to 1200. Common choices include 72 for screens, 150 for drafts, and 300 for commercial printing.",
      },
      {
        number: "03",
        title: "Reprocess Images",
        description:
          "Submit the file and let the tool rewrite the embedded image data toward your requested resolution.",
      },
      {
        number: "04",
        title: "Download Updated PDF",
        description:
          "Save your newly processed binary PDF. The output filename will conveniently include your selected DPI value.",
      },
    ],
    useCases: [
      {
        label: "Print Preparation",
        icon: "book-open",
        description:
          "Ensure marketing materials and brochures hit the required 300 DPI mark.",
        accent: "primary",
      },
      {
        label: "Email Optimization",
        icon: "mail",
        description:
          "Compress heavy scanned contracts down to 72 DPI for easy client sharing.",
        accent: "primary",
      },
      {
        label: "Digital Archiving",
        icon: "archive",
        description:
          "Standardize an assortment of varied documents to a clean 150 DPI format.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Graphic Designers",
        icon: "pen-tool",
        description:
          "Need to force-convert draft files to 300 DPI before sending to the printer.",
        accent: "accent",
      },
      {
        label: "Office Administrators",
        icon: "briefcase",
        description:
          "Looking to quickly reduce PDF DPI to bypass strict corporate email attachment limits.",
        accent: "accent",
      },
      {
        label: "Students & Educators",
        icon: "graduation-cap",
        description:
          "Need to make scanned textbook pages smaller and easier to upload to learning portals.",
        accent: "accent",
      },
    ],
  },
};

export default pdfDpiChangerTool;
export { pdfDpiChangerTool };
